import type { IAttachment, IChangeRequest, IChangeRequestRef } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const base = (projectId: string) => `/api/v1/projects/${projectId}/change-requests`;

export interface CrChangeDetailLineInput {
  changeDetail: string;
  effortHours: number;
  comments?: string;
}
export interface CrRiskAssessmentLineInput {
  risk: string;
  mitigation?: string;
}

export interface CreateChangeRequestInput {
  name: string;
  requestDate?: string;
  priority: string;
  requestedByText: string;
  // `| null` so the same payload object literal can be sent to either create or update — a
  // `null` Linked User on create is equivalent to omitting it (see BusinessUnit's headId for
  // the identical reasoning); required so update can distinguish "leave unchanged" (key
  // omitted) from "clear the Linked User" (`requestedByUserId: null`).
  requestedByUserId?: string | null;
  changeDescription: string;
  reasonForChange: string;
  changeDetailLines?: CrChangeDetailLineInput[];
  impactedAreas?: string[];
  impactDescription?: string | null;
  riskAssessmentLines?: CrRiskAssessmentLineInput[];
  commercials?: string | null;
  comments?: string | null;
}
export type UpdateChangeRequestInput = Partial<CreateChangeRequestInput>;

export async function fetchChangeRequests(projectId: string): Promise<IChangeRequest[]> {
  const res = await apiFetch(base(projectId));
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch change requests'));
  return res.json() as Promise<IChangeRequest[]>;
}

// Approved-only, minimal list for the Task "Change Request" picker — the API applies the
// configured Approved step itself, so callers never compare approvalStatus client-side.
export async function fetchChangeRequestLookup(projectId: string): Promise<IChangeRequestRef[]> {
  const res = await apiFetch(`${base(projectId)}/lookup`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch approved change requests'));
  return res.json() as Promise<IChangeRequestRef[]>;
}

export async function createChangeRequest(projectId: string, data: CreateChangeRequestInput): Promise<IChangeRequest> {
  const res = await apiFetch(base(projectId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create change request'));
  return res.json() as Promise<IChangeRequest>;
}

export async function updateChangeRequest(projectId: string, crId: string, data: UpdateChangeRequestInput): Promise<IChangeRequest> {
  const res = await apiFetch(`${base(projectId)}/${crId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update change request'));
  return res.json() as Promise<IChangeRequest>;
}

export async function updateChangeRequestApprovalStatus(
  projectId: string,
  crId: string,
  data: { approvalStatus: string; approvedById?: string; approverRole?: string },
): Promise<IChangeRequest> {
  const res = await apiFetch(`${base(projectId)}/${crId}/approval-status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update approval status'));
  return res.json() as Promise<IChangeRequest>;
}

export async function promoteRiskLine(projectId: string, crId: string, lineId: string, ownerId: string): Promise<IChangeRequest> {
  const res = await apiFetch(`${base(projectId)}/${crId}/risk-lines/${lineId}/promote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ownerId }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to promote risk row'));
  return res.json() as Promise<IChangeRequest>;
}

export async function setChangeRequestActive(projectId: string, crId: string, active: boolean): Promise<IChangeRequest> {
  const res = await apiFetch(`${base(projectId)}/${crId}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update change request status'));
  return res.json() as Promise<IChangeRequest>;
}

export async function fetchChangeRequestAttachments(projectId: string, crId: string): Promise<IAttachment[]> {
  const res = await apiFetch(`${base(projectId)}/${crId}/attachments`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch attachments'));
  return res.json() as Promise<IAttachment[]>;
}

export async function uploadChangeRequestAttachment(projectId: string, crId: string, file: File): Promise<IAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiFetch(`${base(projectId)}/${crId}/attachments`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to upload attachment'));
  return res.json() as Promise<IAttachment>;
}

export async function removeChangeRequestAttachment(projectId: string, crId: string, attachmentId: string): Promise<void> {
  const res = await apiFetch(`${base(projectId)}/${crId}/attachments/${attachmentId}/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to remove attachment'));
}

export function changeRequestAttachmentDownloadUrl(projectId: string, crId: string, attachmentId: string): string {
  return `${base(projectId)}/${crId}/attachments/${attachmentId}/download`;
}

export function changeRequestDocxExportUrl(projectId: string, crId: string): string {
  return `${base(projectId)}/${crId}/export/docx`;
}
